import { useState } from 'react';
import { Copy, Network, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import { useHwcgFlag } from 'utils/featureFlags';
import { FilledRosterTemplatesSection } from './FilledRosterTemplatesSection';
import {
  useArchiveRosterTemplate,
  useRosterTemplateLibrary,
  type RosterTemplateLibraryEntry,
} from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import { RosterTemplateEditorDialog } from './RosterTemplateEditorDialog';
import {
  cloneTemplateAsDraft,
  type TemplateEditorSeed,
} from './rosterTemplateSeats';
import { isUscgFocRosterTemplateId } from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/uscgFocPlanning';
import { StartFromDialog } from './StartFromDialog';

type EditorState = {
  existing: RosterTemplateLibraryEntry | null;
  seed: TemplateEditorSeed;
};

export function RosterTemplateLibraryView() {
  const hwcg = useHwcgFlag();
  const { data: templates = [], isLoading } = useRosterTemplateLibrary();
  const archiveTemplate = useArchiveRosterTemplate();
  const [starterOpen, setStarterOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [toDelete, setToDelete] = useState<RosterTemplateLibraryEntry | null>(
    null
  );

  return (
    <div
      className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:text-foreground"
      data-tour="foc-roster-library"
    >
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div>
          <div className="tw:text-sm tw:font-medium">Roster Templates</div>
          <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
            Save org chart structures for this organization. They appear in the
            Templates panel on any workspace org chart, alongside the built-in
            ICS templates.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          data-tour="foc-roster-new"
          onClick={() => setStarterOpen(true)}
        >
          <Plus className="tw:size-4" />
          New Template
        </Button>
      </div>

      <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto tw:rounded-md tw:border tw:border-border">
        {isLoading ? (
          <div className="tw:p-3 tw:text-xs tw:text-muted-foreground">
            Loading templates…
          </div>
        ) : templates.length === 0 ? (
          <div className="tw:flex tw:min-h-[16rem] tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:p-6 tw:text-center tw:text-sm tw:text-muted-foreground">
            <Network className="tw:size-6" />
            Create a template here, or save one from a workspace org chart.
          </div>
        ) : (
          templates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              rowTour={
                isUscgFocRosterTemplateId(template.id)
                  ? 'foc-roster-row'
                  : undefined
              }
              busy={archiveTemplate.isPending}
              onEdit={() =>
                setEditor({
                  existing: template,
                  seed: {
                    name: template.name,
                    description: template.description,
                    attachOnly: template.attachOnly,
                    nodes: template.nodes,
                  },
                })
              }
              onDuplicate={() =>
                setEditor({
                  existing: null,
                  seed: cloneTemplateAsDraft(template),
                })
              }
              onArchive={
                isUscgFocRosterTemplateId(template.id)
                  ? undefined
                  : () => setToDelete(template)
              }
            />
          ))
        )}
      </div>

      <FilledRosterTemplatesSection hwcg={hwcg} />

      <ConfirmDeleteDialog
        open={toDelete !== null}
        title="Delete roster template"
        description={
          toDelete
            ? `Delete ${toDelete.name}? It is removed from the Templates panel. Org charts already built from it keep their seats.`
            : ''
        }
        pending={archiveTemplate.isPending}
        onOpenChange={(next) => {
          if (!next) setToDelete(null);
        }}
        onConfirm={() => {
          if (!toDelete) return;
          archiveTemplate.mutate(toDelete.id, {
            onSuccess: () => setToDelete(null),
          });
        }}
      />

      <StartFromDialog
        open={starterOpen}
        onClose={() => setStarterOpen(false)}
        onChoose={({ seed }) => {
          setStarterOpen(false);
          setEditor({ existing: null, seed });
        }}
      />

      {editor ? (
        <RosterTemplateEditorDialog
          existing={editor.existing}
          seed={editor.seed}
          onClose={() => setEditor(null)}
        />
      ) : null}
    </div>
  );
}

function TemplateRow({
  template,
  busy,
  rowTour,
  onEdit,
  onDuplicate,
  onArchive,
}: {
  template: RosterTemplateLibraryEntry;
  busy: boolean;
  rowTour?: string;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive?: () => void;
}) {
  return (
    <div
      className="tw:flex tw:items-start tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0"
      data-tour={rowTour}
    >
      <div className="tw:min-w-0 tw:flex-1">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <span className="tw:text-sm tw:font-medium tw:break-words">
            {template.name}
          </span>
          {template.attachOnly ? (
            <Badge variant="outline" className="tw:text-[10px]">
              expansion
            </Badge>
          ) : null}
          <span className="tw:text-xs tw:text-muted-foreground">
            {template.nodes.length}{' '}
            {template.nodes.length === 1 ? 'seat' : 'seats'}
          </span>
        </div>
        {template.description ? (
          <p className="tw:mt-1 tw:mb-0 tw:text-xs tw:text-muted-foreground">
            {template.description}
          </p>
        ) : null}
      </div>
      <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="tw:size-8"
          aria-label={`Edit ${template.name}`}
          title="Edit"
          onClick={onEdit}
        >
          <Pencil className="tw:size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="tw:size-8"
          aria-label={`Duplicate ${template.name}`}
          title="Duplicate"
          onClick={onDuplicate}
        >
          <Copy className="tw:size-4" />
        </Button>
        {onArchive ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="tw:size-8"
            disabled={busy}
            aria-label={`Delete ${template.name}`}
            title="Delete"
            onClick={onArchive}
          >
            <Trash2 className="tw:size-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
